import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, MessageSquareText, Pencil, Send, ThumbsDown, XCircle } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dateLocale, withClock } from "@/lib/dateTime";
import { SampleStatusBadge, SAMPLE_STATUS_LABELS } from "./SampleStatusBadge";
import type { SampleRecord, SampleStatus } from "./sampleTypes";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "-");
const formatWhen = (d: string) => new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }));
const ACTIONS: Record<string, string> = { CREATED: "Requested", UPDATED: "Details changed", SENT: "Sent", FEEDBACK: "Feedback recorded", APPROVED: "Approved", REJECTED: "Rejected", CANCELLED: "Cancelled" };

type Dialogs = "send" | "feedback" | "approve" | "reject" | "cancel" | "edit" | null;

export function SampleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [s, setS] = useState<SampleRecord | null>(null);
  useRecordCrumb(s?.request_number);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [text, setText] = useState("");
  const [send, setSend] = useState({ courier: "", tracking_number: "", feedback_due: "", access_note: "" });
  const [edit, setEdit] = useState({ ship_to: "", feedback_due: "", notes: "", access_note: "" });

  const load = async () => {
    if (!id) return;
    try {
      setS(unwrapObject<SampleRecord>(await api.get(`/api/Sample/ById/${id}`)));
    } catch (e: any) {
      error("Could not open the request", e?.message || "Not found");
      navigate("/samples");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !s) return <FormSkeleton />;

  const next = s.allowed_next || [];
  const can = (to: SampleStatus) => next.includes(to);
  const canUpdate = hasPermission("sample.update");
  const digital = s.delivery_mode === "DIGITAL";
  const open = (d: Dialogs) => {
    setText("");
    if (d === "send") setSend({ courier: s.courier || "", tracking_number: s.tracking_number || "", feedback_due: s.feedback_due?.slice(0, 10) || "", access_note: s.access_note || "" });
    if (d === "edit") setEdit({ ship_to: s.ship_to || "", feedback_due: s.feedback_due?.slice(0, 10) || "", notes: s.notes || "", access_note: s.access_note || "" });
    setDialog(d);
  };

  const run = async (fn: () => Promise<any>, done: string) => {
    setBusy(true);
    try {
      await fn();
      success(done, "");
      setDialog(null);
      await load();
    } catch (e: any) {
      error("Could not do that", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const line = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between gap-4 text-[13px]"><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{value}</span></div>
  );
  const target = s.deal ? (
    <Link to={`/deals/${s.deal.id}`} className="text-primary hover:underline">{s.deal.name}</Link>
  ) : s.lead ? (
    <Link to={`/leads/${s.lead.id}`} className="text-primary hover:underline">{[[s.lead.first_name, s.lead.last_name].filter(Boolean).join(" "), s.lead.company_name].filter(Boolean).join(" · ")}</Link>
  ) : null;

  const textDialog: Record<string, { title: string; hint: string; label: string; confirm: string; run: () => void; destructive?: boolean }> = {
    feedback: { title: "Record feedback", hint: "What the customer said after trying it.", label: "What did they say? *", confirm: "Save feedback", run: () => run(() => api.patch(`/api/Sample/Feedback/${s.id}`, { note: text.trim() }), "Feedback recorded") },
    approve: { title: "Customer approved the sample", hint: "This can be the stage requirement 'a sample approved by the customer'.", label: "What did they say? *", confirm: "Mark approved", run: () => run(() => api.patch(`/api/Sample/Approve/${s.id}`, { note: text.trim() }), "Sample approved") },
    reject: { title: "Customer rejected the sample", hint: "The reason helps the next quote.", label: "Why? *", confirm: "Mark rejected", destructive: true, run: () => run(() => api.patch(`/api/Sample/Reject/${s.id}`, { note: text.trim() }), "Sample rejected") },
    cancel: { title: `Cancel ${s.request_number}`, hint: "The follow-up task is closed.", label: "Reason *", confirm: "Cancel request", destructive: true, run: () => run(() => api.patch(`/api/Sample/Cancel/${s.id}`, { reason: text.trim() }), "Request cancelled") },
  };
  const td = dialog && textDialog[dialog];

  return (
    <div className="space-y-4 pb-10">
      <section className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3.5">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="space-y-1">
            <Link to="/samples" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="size-3.5" /> All samples</Link>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-[19px] font-semibold leading-tight tracking-tight font-mono">{s.request_number}</h1>
              <SampleStatusBadge status={s.sample_status} overdue={s.overdue} />
            </div>
            <p className="text-[13px] text-muted-foreground">
              {target} {s.account && <>· {s.account.name}</>} {s.owner && `· owner ${s.owner.first_name} ${s.owner.last_name || ""}`}
            </p>
          </div>
          {canUpdate && (
            <div className="flex flex-wrap gap-2">
              {(s.sample_status === "REQUESTED" || s.sample_status === "SENT") && <Button variant="outline" size="sm" onClick={() => open("edit")} disabled={busy}><Pencil className="size-4 mr-1.5" /> Edit</Button>}
              {can("SENT") && <Button size="sm" onClick={() => open("send")} disabled={busy}><Send className="size-4 mr-1.5" /> {digital ? "Start the trial" : "Mark as sent"}</Button>}
              {can("FEEDBACK_RECEIVED") && <Button variant="outline" size="sm" onClick={() => open("feedback")} disabled={busy}><MessageSquareText className="size-4 mr-1.5" /> Record feedback</Button>}
              {can("APPROVED") && <Button variant="outline" size="sm" onClick={() => open("approve")} disabled={busy}><CheckCircle2 className="size-4 mr-1.5" /> Approved</Button>}
              {can("REJECTED") && <Button variant="outline" size="sm" onClick={() => open("reject")} disabled={busy}><ThumbsDown className="size-4 mr-1.5" /> Rejected</Button>}
              {can("CANCELLED") && <Button variant="outline" size="sm" className="text-destructive" onClick={() => open("cancel")} disabled={busy}><XCircle className="size-4 mr-1.5" /> Cancel</Button>}
            </div>
          )}
        </div>
      </section>

      {s.overdue && <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">The customer was due to give feedback on {formatDate(s.feedback_due)}. Ask them, then record what they said.</div>}
      {s.sample_status === "REJECTED" && s.decision_note && <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm">Rejected: {s.decision_note}</div>}
      {s.sample_status === "APPROVED" && <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-sm">Approved{s.decision_note ? `: ${s.decision_note}` : ""}</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-lg border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Quantity</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
              <TableBody>
                {(s.items || []).map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="text-sm font-medium">{i.product_name}{i.variant_label ? ` · ${i.variant_label}` : ""}</TableCell>
                    <TableCell className="text-right text-sm whitespace-nowrap">{Number(i.quantity)} {i.unit}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{i.note || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(s.feedback || s.notes) && (
            <div className="rounded-lg border border-border bg-card p-4 space-y-3 text-[13px]">
              {s.notes && <div><div className="mb-0.5 font-semibold">Note</div><p className="whitespace-pre-wrap text-muted-foreground">{s.notes}</p></div>}
              {s.feedback && <div><div className="mb-0.5 font-semibold">What the customer said{s.feedback_at ? ` (${formatDate(s.feedback_at)})` : ""}</div><p className="whitespace-pre-wrap text-muted-foreground">{s.feedback}</p></div>}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 space-y-2">
            <div className="text-sm font-semibold">{digital ? "Trial" : "Delivery"}</div>
            {line("Requested", formatDate(s.requested_on))}
            {line(digital ? "Started" : "Sent", s.sent_at ? formatDate(s.sent_at) : "-")}
            {!digital && line("Courier", s.courier || "-")}
            {!digital && line("Tracking", s.tracking_number || "-")}
            {line(digital ? "Trial ends" : "Feedback due", s.feedback_due ? formatDate(s.feedback_due) : "-")}
            {digital && s.access_note && <div className="pt-1 text-[13px]"><div className="text-muted-foreground">How they get in</div><p className="whitespace-pre-wrap">{s.access_note}</p></div>}
            {s.ship_to && <div className="pt-1 text-[13px]"><div className="text-muted-foreground">Send to</div><p className="whitespace-pre-wrap">{s.ship_to}</p></div>}
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="mb-3 text-sm font-semibold">History</div>
            <ol className="space-y-3">
              {(s.history || []).map((h) => (
                <li key={h.id} className="text-[13px]">
                  <div className="flex justify-between gap-2"><span className="font-medium">{ACTIONS[h.action] || h.action}</span><span className="text-xs text-muted-foreground whitespace-nowrap">{formatWhen(h.created_on)}</span></div>
                  <div className="text-xs text-muted-foreground">{[h.by, h.from_status && h.to_status && h.from_status !== h.to_status ? `${SAMPLE_STATUS_LABELS[h.from_status as SampleStatus] || h.from_status} → ${SAMPLE_STATUS_LABELS[h.to_status as SampleStatus] || h.to_status}` : null, h.comment].filter(Boolean).join(" · ")}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <Dialog open={!!td} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{td?.title}</DialogTitle><DialogDescription>{td?.hint}</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label>{td?.label}</Label><Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Back</Button>
            <Button variant={td?.destructive ? "destructive" : "default"} onClick={() => td?.run()} disabled={busy || !text.trim()}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}{td?.confirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "send"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{digital ? `Start the trial ${s.request_number}` : `Mark ${s.request_number} as sent`}</DialogTitle><DialogDescription>{digital ? "A follow-up task is made for the day the trial ends." : "A follow-up task is made for the day the customer should have tried it."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {digital && <div className="space-y-1.5"><Label>How they get in</Label><Textarea rows={3} value={send.access_note} onChange={(e) => setSend({ ...send, access_note: e.target.value })} maxLength={1000} placeholder="Trial link, who has access, what to try. Never put a password here." /></div>}
            {!digital && <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Courier</Label><Input value={send.courier} onChange={(e) => setSend({ ...send, courier: e.target.value })} maxLength={100} /></div>
              <div className="space-y-1.5"><Label>Tracking number</Label><Input value={send.tracking_number} onChange={(e) => setSend({ ...send, tracking_number: e.target.value })} maxLength={100} /></div>
            </div>}
            <div className="space-y-1.5"><Label>{digital ? "Trial ends on" : "Ask for feedback on"}</Label><Input type="date" value={send.feedback_due} onChange={(e) => setSend({ ...send, feedback_due: e.target.value })} /><p className="text-[11px] text-muted-foreground">Empty = {digital ? "two weeks" : "a week"} from now</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Back</Button>
            <Button onClick={() => run(() => api.patch(`/api/Sample/Send/${s.id}`, { courier: send.courier.trim() || undefined, tracking_number: send.tracking_number.trim() || undefined, access_note: digital ? send.access_note.trim() || undefined : undefined, feedback_due: send.feedback_due || undefined }), digital ? "Trial started" : "Marked as sent")} disabled={busy || (!digital && !send.courier.trim() && !send.tracking_number.trim())}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}{digital ? "Start the trial" : "Mark as sent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "edit"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit {s.request_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!digital && <div className="space-y-1.5"><Label>Send to</Label><Textarea rows={2} value={edit.ship_to} onChange={(e) => setEdit({ ...edit, ship_to: e.target.value })} maxLength={1000} /></div>}
            {digital && <div className="space-y-1.5"><Label>How they get in</Label><Textarea rows={2} value={edit.access_note} onChange={(e) => setEdit({ ...edit, access_note: e.target.value })} maxLength={1000} /></div>}
            <div className="space-y-1.5"><Label>{digital ? "Trial ends" : "Feedback due"}</Label><Input type="date" value={edit.feedback_due} onChange={(e) => setEdit({ ...edit, feedback_due: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} maxLength={2000} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Back</Button>
            <Button onClick={() => run(() => api.put(`/api/Sample/Update/${s.id}`, { ship_to: digital ? undefined : edit.ship_to.trim(), access_note: digital ? edit.access_note.trim() : undefined, feedback_due: edit.feedback_due || undefined, notes: edit.notes.trim() }), "Saved")} disabled={busy}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
